/**
 * Screen 2 — "New upload": address + two clearly separate, labelled drop zones (report +
 * letter of instruction). Submit stays disabled until the address and BOTH valid files are
 * present, which guarantees a complete job. After submit: a calm confirmation screen.
 */
import { useEffect, useState } from 'react';
import { Button, Tile, Banner } from '../components/ui';
import { DropZone } from '../components/DropZone';
import { uploadReport, fetchEta, ApiError, type UploadResult } from '../api';
import { validateFile, type FileProblem } from '../lib/files';
import { valuerEtaSentence, type EtaInfo } from '../lib/eta';
import { dateInputValueToIso, endOfThisWeek, isPastDate, toDateInputValue, today, tomorrow } from '../lib/deadline';

type Phase = 'form' | 'uploading' | 'done';

interface NewUploadProps {
  onList: () => void;
  onSessionExpired: () => void;
}

const ADDRESS_HINT = 'e.g. 12 Example St, Suburb NSW 2000';

export function NewUpload({ onList, onSessionExpired }: NewUploadProps) {
  const [address, setAddress] = useState('');
  const [addressTouched, setAddressTouched] = useState(false);
  const [report, setReport] = useState<File | null>(null);
  const [reportProblem, setReportProblem] = useState<FileProblem>(null);
  const [loi, setLoi] = useState<File | null>(null);
  const [loiProblem, setLoiProblem] = useState<FileProblem>(null);
  const [neededBy, setNeededBy] = useState(''); // yyyy-mm-dd, optional
  const [notes, setNotes] = useState(''); // optional, shown to the reviewer above the report summary

  const [phase, setPhase] = useState<Phase>('form');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [eta, setEta] = useState<EtaInfo | null>(null);

  // Part IX: fetch the rolling processing estimate for the confirmation screen.
  useEffect(() => {
    if (phase !== 'done') return;
    let cancelled = false;
    fetchEta()
      .then((info) => {
        if (!cancelled) setEta(info);
      })
      .catch(() => {
        if (!cancelled) setEta(null);
      });
    return () => {
      cancelled = true;
    };
  }, [phase]);

  const addressValid = address.trim().length >= 5;
  const neededByPast = isPastDate(neededBy);
  const busy = phase === 'uploading';
  const canSubmit =
    addressValid && !!report && !reportProblem && !!loi && !loiProblem && phase === 'form';

  const chooseReport = (file: File) => {
    const problem = validateFile(file);
    setReportProblem(problem);
    setReport(problem ? null : file);
  };
  const chooseLoi = (file: File) => {
    const problem = validateFile(file);
    setLoiProblem(problem);
    setLoi(problem ? null : file);
  };

  const resetForm = () => {
    setAddress('');
    setAddressTouched(false);
    setReport(null);
    setReportProblem(null);
    setLoi(null);
    setLoiProblem(null);
    setNeededBy('');
    setNotes('');
    setProgress(0);
    setError(null);
    setResult(null);
    setPhase('form');
  };

  const submit = async () => {
    if (!canSubmit || !report || !loi) return;
    setError(null);
    setProgress(0);
    setPhase('uploading');
    try {
      const res = await uploadReport({
        address: address.trim(),
        report,
        letterOfInstruction: loi,
        // A past date is rejected (see the inline message below), not sent - the field
        // stays optional either way, so this never blocks the submit itself.
        neededByAt: neededBy && !isPastDate(neededBy) ? dateInputValueToIso(neededBy) : null,
        notes: notes.trim() || null,
        onProgress: setProgress,
      });
      setResult(res);
      setPhase('done');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'session_expired') {
        onSessionExpired();
        return;
      }
      setError(err instanceof Error ? err.message : 'The upload didn’t go through. Please try again.');
      setPhase('form');
    }
  };

  // --- Confirmation -----------------------------------------------------------
  if (phase === 'done' && result) {
    const duplicate = result.duplicate;
    return (
      <main className="main">
        <Tile className="tile--pad-lg">
          <div className="confirm">
            <div className="confirm__mark" aria-hidden="true">
              ✓
            </div>
            <h1 className="confirm__title">{duplicate ? 'Already received' : 'Thanks — that’s done'}</h1>
            <p className="confirm__body">
              {duplicate ? (
                <>
                  You’ve already uploaded these files for <strong>{result.address}</strong>. It’s
                  already with us — there’s nothing more you need to do.
                </>
              ) : (
                <>
                  Your report for <strong>{result.address}</strong> is now being reviewed
                  automatically. You don’t need to do anything else — we’ll take it from here.
                </>
              )}
            </p>
            {!duplicate && eta ? <p className="confirm__body muted">{valuerEtaSentence(eta)}</p> : null}
            <div className="confirm__actions">
              <Button onClick={resetForm}>Upload another</Button>
              <Button variant="secondary" onClick={onList}>
                Back to my uploads
              </Button>
            </div>
          </div>
        </Tile>
      </main>
    );
  }

  // --- Form -------------------------------------------------------------------
  return (
    <main className="main">
      <div className="page-head">
        <h1 className="page-title">New upload</h1>
        <Button variant="ghost" onClick={onList} disabled={busy}>
          ← Back to my uploads
        </Button>
      </div>

      <Tile className="tile--pad-lg">
        <div className="stack">
          {error ? <Banner tone="error">{error}</Banner> : null}

          <div className="field">
            <label className="field__label" htmlFor="address">
              Property address
            </label>
            <input
              id="address"
              className="input"
              type="text"
              inputMode="text"
              placeholder={ADDRESS_HINT}
              aria-describedby="address-hint"
              aria-invalid={addressTouched && !addressValid ? true : undefined}
              value={address}
              disabled={busy}
              onChange={(e) => setAddress(e.target.value)}
              onBlur={() => setAddressTouched(true)}
            />
            {addressTouched && !addressValid ? (
              <span className="field__error" role="alert">
                Please enter the property address.
              </span>
            ) : (
              <span className="field__hint" id="address-hint">
                {ADDRESS_HINT}
              </span>
            )}
          </div>

          <div className="field">
            <label className="field__label" htmlFor="needed-by">
              Needed by <span className="field__optional">(optional)</span>
            </label>
            <input
              id="needed-by"
              className="input"
              type="date"
              min={toDateInputValue(today())}
              value={neededBy}
              disabled={busy}
              aria-invalid={neededByPast ? true : undefined}
              aria-describedby="needed-by-hint"
              onChange={(e) => setNeededBy(e.target.value)}
            />
            <div className="quick-picks">
              <button type="button" className="quick-pick" disabled={busy} onClick={() => setNeededBy(toDateInputValue(today()))}>
                Today
              </button>
              <button type="button" className="quick-pick" disabled={busy} onClick={() => setNeededBy(toDateInputValue(tomorrow()))}>
                Tomorrow
              </button>
              <button type="button" className="quick-pick" disabled={busy} onClick={() => setNeededBy(toDateInputValue(endOfThisWeek()))}>
                This week
              </button>
              {neededBy ? (
                <button type="button" className="quick-pick" disabled={busy} onClick={() => setNeededBy('')}>
                  Clear
                </button>
              ) : null}
            </div>
            {neededByPast ? (
              <span className="field__error" role="alert">
                That date has already passed — pick today or later.
              </span>
            ) : (
              <span className="field__hint" id="needed-by-hint">
                When you need the review back by, if there's a deadline.
              </span>
            )}
          </div>

          <div className="field">
            <label className="field__label" htmlFor="notes">
              Note for the reviewer <span className="field__optional">(optional)</span>
            </label>
            <textarea
              id="notes"
              className="input note-textarea"
              placeholder="Anything the reviewer should know about this report…"
              rows={3}
              maxLength={2000}
              value={notes}
              disabled={busy}
              onChange={(e) => setNotes(e.target.value)}
            />
            <span className="field__hint">Shown to the reviewer above the report summary.</span>
          </div>

          <div className="dz-group">
            <DropZone
              id="report"
              title="Valuation report"
              file={report}
              problem={reportProblem}
              disabled={busy}
              onFile={chooseReport}
              onClear={() => {
                setReport(null);
                setReportProblem(null);
              }}
            />
            <DropZone
              id="loi"
              title="Letter of instruction"
              file={loi}
              problem={loiProblem}
              disabled={busy}
              onFile={chooseLoi}
              onClear={() => {
                setLoi(null);
                setLoiProblem(null);
              }}
            />
          </div>

          {busy ? (
            <div className="stack" aria-live="polite">
              <div className="progress">
                <div className="progress__bar" style={{ width: `${progress}%` }} />
              </div>
              <span className="muted">Uploading your files… {progress}%</span>
            </div>
          ) : (
            <Button onClick={submit} disabled={!canSubmit} block>
              Submit for review
            </Button>
          )}

          {!busy ? (
            <p className="field__hint">
              Submit becomes available once the address and both files are attached.
            </p>
          ) : null}
        </div>
      </Tile>
    </main>
  );
}
